/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { inject, Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface NotificacionRecibida {
  id: string;
  titulo: string;
  mensaje: string;
  fecha: Date;
  leida: boolean;
  tipo: 'success' | 'info' | 'warning' | 'error';
}

declare global {
  interface Window {
    OneSignal: any;
    OneSignalDeferred?: Array<(OneSignal: any) => void | Promise<void>>;
    guardarNotificacionAuto?: (titulo: string, mensaje: string, id: string) => unknown;
  }
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private subscriptionStatus = new BehaviorSubject<boolean>(false);
  private notificacionRecibida = new Subject<NotificacionRecibida>();
  public onNotificacionRecibida = this.notificacionRecibida.asObservable();
  private oneSignal: any = null;
  private initialized = false;
  private readonly ngZone = inject(NgZone);

  constructor() {
    this.initOneSignal();
    this.setupServiceWorkerListener();
    this.setupAutoCapture();
  }

  private setupServiceWorkerListener() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
          this.ngZone.run(() => {
            const notification = event.data.notification;

            const notificacion: NotificacionRecibida = {
              id: notification.id || Date.now().toString(),
              titulo: notification.titulo || 'Notificación',
              mensaje: notification.mensaje || '',
              fecha: new Date(notification.fecha || new Date()),
              leida: false,
              tipo: notification.tipo || 'info',
            };

            this.notificacionRecibida.next(notificacion);
            this.guardarEnLocalStorage(notificacion);
          });
        }
      });
    }
  }

  private setupAutoCapture() {
    if (typeof window !== 'undefined') {
      (window as any).guardarNotificacionAuto = (titulo: string, mensaje: string, id: string) => {
        const notificacion: NotificacionRecibida = {
          id: id || Date.now().toString(),
          titulo: titulo || 'Notificación',
          mensaje: mensaje || '',
          fecha: new Date(),
          leida: false,
          tipo: 'info',
        };

        this.ngZone.run(() => {
          this.notificacionRecibida.next(notificacion);
          this.guardarEnLocalStorage(notificacion);
        });

        return notificacion;
      };
    }
  }

  private async initOneSignal() {
    try {
      await this.waitForOneSignal();
      this.oneSignal = window.OneSignal;

      const isSubscribed = await this.oneSignal.Notifications.permission;
      this.subscriptionStatus.next(isSubscribed);
      this.initialized = true;

      if (this.oneSignal.Notifications) {
        this.oneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
          this.ngZone.run(() => {
            const notification = event.notification;
            const notificacion: NotificacionRecibida = {
              id: notification.notificationId || Date.now().toString(),
              titulo: notification.title || 'Notificación',
              mensaje: notification.body || '',
              fecha: new Date(),
              leida: false,
              tipo: this.determinarTipo(notification),
            };

            this.notificacionRecibida.next(notificacion);
            this.guardarEnLocalStorage(notificacion);

            if (event.preventDefault) event.preventDefault();
          });
        });

        this.oneSignal.Notifications.addEventListener('notificationClick', (event: any) => {
          this.ngZone.run(() => {
            const notification = event.notification;
            const notificacion: NotificacionRecibida = {
              id: notification.notificationId || Date.now().toString(),
              titulo: notification.title || 'Notificación',
              mensaje: notification.body || '',
              fecha: new Date(),
              leida: false,
              tipo: this.determinarTipo(notification),
            };

            this.notificacionRecibida.next(notificacion);
            this.guardarEnLocalStorage(notificacion);
          });
        });
      }
    } catch (error) {
      this.fallbackToNative();
    }
  }

  private guardarEnLocalStorage(notificacion: NotificacionRecibida) {
    try {
      const existentes = JSON.parse(localStorage.getItem('notificaciones_reales') || '[]');
      const nuevaNotificacion = {
        id: notificacion.id,
        titulo: notificacion.titulo,
        mensaje: notificacion.mensaje,
        fecha: notificacion.fecha,
        leida: false,
        tipo: notificacion.tipo,
      };

      existentes.unshift(nuevaNotificacion);
      const final = existentes.slice(0, 100);
      localStorage.setItem('notificaciones_reales', JSON.stringify(final));
    } catch (error) {}
  }

  private determinarTipo(notification: any): 'success' | 'info' | 'warning' | 'error' {
    const text = (notification.body || '').toLowerCase();
    const title = (notification.title || '').toLowerCase();

    if (text.includes('error') || text.includes('fallo') || title.includes('error')) return 'error';
    if (text.includes('warning') || text.includes('advertencia')) return 'warning';
    if (text.includes('éxito') || text.includes('completado') || text.includes('success'))
      return 'success';
    return 'info';
  }

  private waitForOneSignal(): Promise<void> {
    return new Promise((resolve) => {
      if (window.OneSignal && window.OneSignal.Notifications) {
        resolve();
        return;
      }

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (window.OneSignal && window.OneSignal.Notifications) {
          clearInterval(interval);
          resolve();
        } else if (attempts > 100) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }

  private fallbackToNative() {
    const hasPermission = localStorage.getItem('notification_permission') === 'granted';
    this.subscriptionStatus.next(hasPermission);
  }

  async isSubscribed(): Promise<boolean> {
    if (this.initialized && this.oneSignal) {
      try {
        return await this.oneSignal.Notifications.permission;
      } catch (error) {
        return Notification.permission === 'granted';
      }
    }
    return Notification.permission === 'granted';
  }

  async subscribe(): Promise<boolean> {
    if (this.initialized && this.oneSignal) {
      try {
        const permission = await this.oneSignal.Notifications.requestPermission();
        const success = permission === true;

        if (success) {
          this.subscriptionStatus.next(true);
          localStorage.setItem('notification_permission', 'granted');
        }

        return success;
      } catch (error) {}
    }

    const permission = await Notification.requestPermission();
    const success = permission === 'granted';
    if (success) {
      this.subscriptionStatus.next(true);
      localStorage.setItem('notification_permission', 'granted');
    }
    return success;
  }

  async unsubscribe(): Promise<boolean> {
    this.subscriptionStatus.next(false);
    localStorage.setItem('notification_permission', 'denied');
    return true;
  }

  getSubscriptionStatus(): Observable<boolean> {
    return this.subscriptionStatus.asObservable();
  }

  async sincronizarNotificaciones(): Promise<number> {
    try {
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration && registration.active) {
          registration.active.postMessage({ type: 'GET_PENDING_NOTIFICATIONS' });

          const pendingNotifications = await new Promise<any[]>((resolve) => {
            const handler = (event: MessageEvent) => {
              if (event.data && event.data.type === 'PENDING_NOTIFICATIONS') {
                navigator.serviceWorker.removeEventListener('message', handler);
                resolve(event.data.notifications || []);
              }
            };
            navigator.serviceWorker.addEventListener('message', handler);

            setTimeout(() => resolve([]), 3000);
          });

          if (pendingNotifications.length > 0) {
            pendingNotifications.forEach((notif: any) => {
              this.notificacionRecibida.next({
                id: notif.id,
                titulo: notif.titulo,
                mensaje: notif.mensaje,
                fecha: new Date(notif.fecha),
                leida: false,
                tipo: 'info',
              });
            });
            return pendingNotifications.length;
          }
        }
      }
      return 0;
    } catch (error) {
      return 0;
    }
  }

  testNotification(titulo: string, mensaje: string) {
    const notificacion: NotificacionRecibida = {
      id: 'test-' + Date.now(),
      titulo: titulo || '🔧 Notificación de Prueba',
      mensaje: mensaje || 'Esta es una notificación de prueba del sistema',
      fecha: new Date(),
      leida: false,
      tipo: 'success',
    };

    this.notificacionRecibida.next(notificacion);
    this.guardarEnLocalStorage(notificacion);
  }
}
